import { MAX_EVENT_HIGHLIGHTS_LIMIT, MAX_PACKAGES_LIMIT } from '@/constants'
import { AdvertisementPlacements } from '@/constants/sponsor'
import { SPONSORSHIP_PLANS } from '@/constants/sponsorshipPlans'
import { urlRegex } from '@/constants/validation'
import { normalizeDate } from '@/utils/dateUtils'
import { validateEmail, validatePhoneNumber } from '@/utils/helper'
import { z } from 'zod'

export const zodPriceSchema = (fieldName: string) => {
  return z
    .string()
    .optional()
    .nullable()
    .refine(
      data => {
        return !(Number(data) < 0)
      },
      {
        message: `${fieldName} price must be greater or equal to 0`,
      }
    )
    .refine(
      data => {
        return !(Number(data) > 99999999.99)
      },
      {
        message: `${fieldName} price must not exceed $99,999,999.99`,
      }
    )
}

export const zodMaxAttendeeSchema = (fieldName: string) => {
  return z
    .string()
    .nonempty('Max attendees limit is required')
    .refine(
      data => {
        return !(Number(data) <= 0)
      },
      {
        message: `${fieldName} must be greater then 0`,
      }
    )
    .refine(
      data => {
        return !(Number(data) > 100000)
      },
      {
        message: `${fieldName} must not exceed 100,000`,
      }
    )
}

export const zodEventSponsorShipSchema = (fieldName: string) => {
  return z.object({
    id: z.string().optional(),
    name: z.string(),
    price: zodPriceSchema(fieldName),
  })
}

export const zodEventSponsorShipWithHeroSchema = (fieldName: string) => {
  return zodEventSponsorShipSchema(fieldName)
    .extend({
      heroDuration: z
        .string()
        .optional()
        .refine(
          data => {
            if (!data) return true
            return !(Number(data) <= 0)
          },
          {
            message: `Hero Flight Duration must be greater than 0`,
          }
        ),
    })
    .refine(
      data => {
        const hasPrice =
          data.price !== null &&
          data.price !== undefined &&
          data.price !== '' &&
          Number(data.price) > 0

        if (!hasPrice) return true

        return data.heroDuration && data.heroDuration.trim() !== ''
      },
      {
        message: 'Hero Flight Duration is required',
        path: ['heroDuration'],
      }
    )
}

export const adSloptsSchema = z.object({
  id: z.string(),
  name: z.string(),
})

export const hasPackageData = (pkg: {
  name?: string
  description?: string
  price?: string | null
  availablePlacements?: string[]
  priority?: string
  heroDuration?: string | null
  maxSponsorshipsLimit?: string | null
}) => {
  return !!(
    pkg.name?.trim() ||
    pkg.description?.trim() ||
    pkg.price ||
    (pkg.availablePlacements && pkg.availablePlacements.length > 0) ||
    pkg.priority?.trim() ||
    pkg.heroDuration?.trim() ||
    pkg.maxSponsorshipsLimit?.trim()
  )
}

export const eventSponsorshipPackageSchema = z
  .object({
    id: z.string().optional(),
    name: z.string(),
    description: z.string(),
    price: z.string().optional().nullable(),
    availablePlacements: z.array(z.string()),
    priority: z.string(),
    heroDuration: z.string().optional().nullable(),
    maxSponsorshipsLimit: z.string().optional().nullable(),
    isNew: z.boolean().optional(),
    isBooked: z.boolean().optional(),
    createdAt: z.string().optional().nullable(),
  })
  .superRefine((pkg, ctx) => {
    const hasData = hasPackageData(pkg)

    if (!hasData) return

    if (!pkg.name?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package name is required',
        path: ['name'],
      })
    } else if (pkg.name.length > 255) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package name must be at most 255 characters',
        path: ['name'],
      })
    }

    // Validate description
    if (!pkg.description?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package description is required',
        path: ['description'],
      })
    } else if (pkg.description.length > 500) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package description must be at most 500 characters',
        path: ['description'],
      })
    }

    // Validate priority
    if (!pkg.priority?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package priority is required',
        path: ['priority'],
      })
    } else if (Number(pkg.priority) <= 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'Package priority must be greater than 0',
        path: ['priority'],
      })
    } else if (Number(pkg.priority) > MAX_PACKAGES_LIMIT) {
      ctx.addIssue({
        code: 'custom',
        message: `Package priority must not exceed ${MAX_PACKAGES_LIMIT}`,
        path: ['priority'],
      })
    }

    if (pkg.price?.trim()) {
      const priceNum = Number(pkg.price)
      if (priceNum < 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Package price must be greater or equal to 0',
          path: ['price'],
        })
      } else if (priceNum > 99999999.99) {
        ctx.addIssue({
          code: 'custom',
          message: 'Package price must not exceed $99,999,999.99',
          path: ['price'],
        })
      }
    } else {
      ctx.addIssue({
        code: 'custom',
        message: 'Package price is required',
        path: ['price'],
      })
    }

    if (pkg.heroDuration?.trim()) {
      const duration = Number(pkg.heroDuration)
      if (duration <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Hero Flight Duration must be greater than 0',
          path: ['heroDuration'],
        })
      } else if (duration > 100000) {
        ctx.addIssue({
          code: 'custom',
          message: 'Hero Flight Duration must not exceed 100,000',
          path: ['heroDuration'],
        })
      }
    }

    if (!(pkg.availablePlacements && pkg.availablePlacements.length > 0)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Atleast one advertisement slot is required',
        path: ['availablePlacements'],
      })
    }

    const hasHeroAds =
      pkg.availablePlacements?.some(
        slot => slot === AdvertisementPlacements.HERO
      ) || false

    if (hasHeroAds && (!pkg.heroDuration || !pkg.heroDuration.trim())) {
      ctx.addIssue({
        code: 'custom',
        message: 'Hero Flight Duration is required when Hero ads is selected',
        path: ['heroDuration'],
      })
    }

    if (pkg.maxSponsorshipsLimit?.trim()) {
      const limit = Number(pkg.maxSponsorshipsLimit)
      if (!Number.isInteger(limit) || limit <= 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'Max Sponsorships Limit must be a positive integer',
          path: ['maxSponsorshipsLimit'],
        })
      } else if (limit > 100) {
        ctx.addIssue({
          code: 'custom',
          message: 'Max Sponsorships Limit must not exceed 100',
          path: ['maxSponsorshipsLimit'],
        })
      }
    } else {
      ctx.addIssue({
        code: 'custom',
        message: 'Max Sponsorships Limit is required',
        path: ['maxSponsorshipsLimit'],
      })
    }
  })

export const eventUserSchema = z
  .object({
    id: z.string(),
    fullName: z.string().max(255, 'Full name must be at most 255 characters'),
    designation: z
      .string()
      .max(255, 'Designation must be at most 255 characters'),
    profile: z.string().optional(),
    avatarFile: z.instanceof(File).optional(),
    isNew: z.boolean().optional(),
  })
  .refine(
    user => {
      const hasData =
        user.fullName || user.designation || user.avatarFile || user.profile
      if (!hasData) return true

      return user.fullName?.trim()
    },
    {
      message: 'Full name is required',
      path: ['fullName'],
    }
  )
  .refine(
    user => {
      const hasData =
        user.fullName || user.designation || user.avatarFile || user.profile
      if (!hasData) return true

      return user.designation?.trim()
    },
    {
      message: 'Designation is required',
      path: ['designation'],
    }
  )
  .refine(
    user => {
      const hasData =
        user.fullName || user.designation || user.avatarFile || user.profile
      if (!hasData) return true

      return user.avatarFile || user.profile
    },
    {
      message: 'Profile image is required',
      path: ['avatarFile'],
    }
  )

// Ticket schema
export const eventTicketSchema = z
  .object({
    id: z.string().optional(),
    name: z
      .string({ error: 'Ticket name is required' })
      .min(1, 'Ticket name is required')
      .max(255, 'Ticket name must be at most 255 characters'),
    description: z
      .string({ error: 'Ticket description is required' })
      .min(1, 'Ticket description is required')
      .max(500, 'Ticket description must be at most 500 characters'),
    perks: z
      .string({ error: 'Ticket perks are required' })
      .min(1, 'Ticket perks are required'),
    price: z.string().nonempty('Ticket price is required').nullable(),
    isNew: z.boolean().optional(),
    maxAttendeesLimit: zodMaxAttendeeSchema('Max attendees limit'),
    isSubEventTicket: z.boolean().optional(),
    subEventId: z.string().optional().nullable(),
    isBooked: z.boolean().optional(),
  })
  .superRefine((ticket, ctx) => {
    if (
      ticket.price === null ||
      ticket.price === undefined ||
      Number(ticket.price) < 0
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Ticket price must be greater or equal to 0',
        path: ['price'],
      })
    } else if (
      ticket.price === null ||
      ticket.price === undefined ||
      Number(ticket.price) > 99999999.99
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Ticket price must not exceed $99,999,999.99',
        path: ['price'],
      })
    }
  })

export const eventImageSchema = z.object({
  id: z.string().optional(),
  key: z.string(),
  url: z.string().optional(),
  index: z.number().optional(),
  imageFile: z.instanceof(File),
  isNew: z.boolean().optional(),
})

export const eventPDFSchema = z
  .object({
    id: z.string().optional(),
    key: z.string(),
    url: z.string().optional(),
    pdfFile: z.instanceof(File),
    isNew: z.boolean().optional(),
  })
  .nullable()

export const eventTagSchema = z.object({
  id: z.string(),
  name: z.string(),
})

export const organizerSchema = z
  .object(
    {
      id: z.string(),
      email: z.string().max(255, 'Email must be at most 255 characters'),
      firstName: z.string(),
      lastName: z.string(),
      phoneNumber: z.string(),
    },
    { error: 'Organizer Name is required' }
  )
  .nullable()
  .refine(
    organizer => {
      return organizer?.id
    },
    {
      error: 'Organizer Name is required',
    }
  )

export const hasHighlightData = (highlight: {
  title?: string
  shortDescription?: string
  eventImage?: string | null
  imageFile?: File
  startDate?: Date
  endDate?: Date
}) => {
  return !!(
    highlight.title?.trim() ||
    highlight.shortDescription?.trim() ||
    highlight.eventImage ||
    highlight.imageFile ||
    highlight.startDate ||
    highlight.endDate
  )
}

export const eventHighlightSchema = z
  .object({
    id: z.string().optional(),
    title: z.string(),
    shortDescription: z.string(),
    eventImage: z.string().optional().nullable(),
    imageFile: z.instanceof(File).optional(),
    startDate: z.date().optional(),
    startTime: z.date().optional(),
    endDate: z.date().optional(),
    endTime: z.date().optional(),
    isNew: z.boolean().optional(),
  })
  .superRefine((highlight, ctx) => {
    const hasData = hasHighlightData(highlight)

    // If no data is filled, skip all validation
    if (!hasData) return

    // Validate title
    if (!highlight.title?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Event highlight title is required',
        path: ['title'],
      })
    } else if (highlight.title.length > 255) {
      ctx.addIssue({
        code: 'custom',
        message: 'Event highlight title must be at most 255 characters',
        path: ['title'],
      })
    }

    if (!highlight.shortDescription?.trim()) {
      ctx.addIssue({
        code: 'custom',
        message: 'Event highlight description is required',
        path: ['shortDescription'],
      })
    } else if (highlight.shortDescription.length > 500) {
      ctx.addIssue({
        code: 'custom',
        message: 'Event highlight description must be at most 500 characters',
        path: ['shortDescription'],
      })
    }

    if (highlight.startDate && !highlight.endDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date is required',
        path: ['endDate'],
      })
    }

    if (highlight.endDate && !highlight.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start date is required',
        path: ['startDate'],
      })
    }

    if (highlight.startDate && !highlight.startTime) {
      ctx.addIssue({
        code: 'custom',
        message: 'Start time is required',
        path: ['startTime'],
      })
    }

    if (highlight.endDate && !highlight.endTime) {
      ctx.addIssue({
        code: 'custom',
        message: 'End time is required',
        path: ['endTime'],
      })
    }

    if (highlight.startDate && highlight.endDate) {
      const start = normalizeDate(highlight.startDate)
      const end = normalizeDate(highlight.endDate)
      if (end < start) {
        ctx.addIssue({
          code: 'custom',
          message: 'End date must be on or after start date',
          path: ['endDate'],
        })
      }

      if (
        highlight.startTime &&
        highlight.endTime &&
        start.getTime() === end.getTime()
      ) {
        if (highlight.endTime.getTime() <= highlight.startTime.getTime()) {
          ctx.addIssue({
            code: 'custom',
            message: 'End time must be after start time',
            path: ['endTime'],
          })
        }
      }
    }
  })

export const baseEventFormSchema = z.object({
  eventName: z
    .string()
    .min(1, 'Event name is required')
    .max(255, 'Event name must be at most 255 characters'),
  description: z.string().min(1, 'Event description is required'),
  category: z.string().min(1, 'Category is required'),
  tag: z.array(eventTagSchema).max(10, 'Maximum 10 tags allowed'),
  eventImages: z
    .array(eventImageSchema)
    .min(1, 'At least one event image is required')
    .max(10, 'Maximum 10 event images allowed'),
  eventPDF: eventPDFSchema.optional().nullable(),
  eventInfoPDF: eventPDFSchema.optional().nullable(),
  startDate: z.date().optional(),
  startTime: z.date().optional(),
  endDate: z.date().optional(),
  endTime: z.date().optional(),
  timeZone: z
    .string({
      message: 'Time Zone is required',
    })
    .min(1, 'Time Zone is required'),
  registrationDeadline: z.date().optional(),
  organizer: organizerSchema,
  organizerEmail: z
    .string()
    .min(1, 'Organizer email is required')
    .refine(
      email => {
        const error = validateEmail(email)
        return error === ''
      },
      {
        message: 'Please enter a valid email address',
      }
    ),
  organizerPhone: z
    .string()
    .min(1, 'Organizer phone is required')
    .refine(
      phone => {
        const error = validatePhoneNumber(phone)
        return error === ''
      },
      {
        message: 'Please enter a valid phone number',
      }
    ),
  organizerWebsite: z.string(),
  sponsorshipPackages: z.array(eventSponsorshipPackageSchema),
  specialInstructions: z.string(),
  users: z.array(eventUserSchema),
  eventHighlights: z
    .array(eventHighlightSchema)
    .max(
      MAX_EVENT_HIGHLIGHTS_LIMIT,
      `Maximum ${MAX_EVENT_HIGHLIGHTS_LIMIT} event highlights allowed`
    ),
})

// Location-specific schemas using discriminated unions
export const onlineEventSchema = baseEventFormSchema.extend({
  location: z.literal('online'),
  zoomLink: z.string().min(1, 'Meeting link is required').regex(urlRegex, {
    message: 'Please enter a valid URL for meeting link',
  }),

  venueName: z.string().optional(),
  venueAddress: z.string().optional(),
  venueCity: z.string().optional(),
  venueState: z.string().optional(),
  venueCountry: z.string().optional(),
  venueLatitude: z.number().optional().nullable(),
  venueLongitude: z.number().optional().nullable(),
  venueZipCode: z.string().optional().nullable(),
})

export const offlineEventSchema = baseEventFormSchema.extend({
  location: z.literal('offline'),
  venueName: z
    .string()
    .min(1, 'Venue name is required')
    .max(255, 'Venue name must be at most 255 characters'),
  venueAddress: z
    .string()
    .min(1, 'Venue address is required')
    .max(255, 'Venue address must be at most 255 characters'),
  venueCity: z
    .string()
    .min(1, 'Venue city is required')
    .max(255, 'Venue city must be at most 255 characters'),
  venueState: z
    .string()
    .min(1, 'Venue state is required')
    .max(255, 'Venue state must be at most 255 characters'),
  venueCountry: z
    .string()
    .min(1, 'Venue country is required')
    .max(255, 'Venue country must be at most 255 characters'),
  venueLatitude: z
    .number({
      message: 'Venue Latitude is required',
    })
    .nullable(),
  venueLongitude: z
    .number({
      message: 'Venue Longitude is required',
    })
    .nullable(),
  venueZipCode: z.string().optional().nullable(),
  zoomLink: z.string().optional(),
})

export const hybridEventSchema = baseEventFormSchema.extend({
  location: z.literal('hybrid'),
  zoomLink: z.string().min(1, 'Meeting link is required').regex(urlRegex, {
    message: 'Please enter a valid URL for meeting link',
  }),
  venueName: z
    .string()
    .min(1, 'Venue name is required')
    .max(255, 'Venue name must be at most 255 characters'),
  venueAddress: z
    .string()
    .min(1, 'Venue address is required')
    .max(255, 'Venue address must be at most 255 characters'),
  venueCity: z
    .string()
    .min(1, 'Venue city is required')
    .max(255, 'Venue city must be at most 255 characters'),
  venueState: z
    .string()
    .min(1, 'Venue state is required')
    .max(255, 'Venue state must be at most 255 characters'),
  venueCountry: z
    .string()
    .min(1, 'Venue country is required')
    .max(255, 'Venue country must be at most 255 characters'),
  venueLatitude: z
    .number({
      message: 'Venue Latitude is required',
    })
    .nullable(),
  venueLongitude: z
    .number({
      message: 'Venue Longitude is required',
    })
    .nullable(),
  venueZipCode: z.string().optional().nullable(),
})

// Location union schema
export const locationBasedEventSchema = z.discriminatedUnion('location', [
  onlineEventSchema,
  offlineEventSchema,
  hybridEventSchema,
])

// Registration-specific schemas using discriminated unions
export const freeRegistrationSchema = z.object({
  registrationType: z.literal('free'),
  eventTicket: z.array(
    z
      .object({
        id: z.string().optional(),
        name: z.string().optional(),
        description: z.string().optional(),
        perks: z.string().optional(),
        price: z.string().optional().nullable(),
        isNew: z.boolean().optional(),
        maxAttendeesLimit: z.string().optional(),
        isSubEventTicket: z.boolean().optional(),
        isBooked: z.boolean().optional(),
      })
      .partial()
  ),
  maxAttendees: z
    .number({ error: 'Max attendees limit is required' })
    .int('Max attendees limit should be in integer')
    .min(1, 'Max attendees must be at least 1')
    .max(100000, 'Max attendees must be at most 100000'),
  ticketName: z
    .string({ error: 'Ticket name is required' })
    .min(1, 'Ticket name is required')
    .max(255, 'Ticket name must be at most 255 characters'),
  ticketDescription: z
    .string({ error: 'Ticket description is required' })
    .min(1, 'Ticket description is required')
    .max(500, 'Ticket description must be at most 500 characters'),
  ticketPerks: z
    .string({ error: 'Ticket perks are required' })
    .min(1, 'Ticket perks are required'),
})

export const paidRegistrationSchema = z.object({
  registrationType: z.literal('paid'),
  maxAttendees: z.number().optional().nullable(),
  ticketName: z.string().optional().nullable(),
  ticketDescription: z.string().optional().nullable(),
  ticketPerks: z.string().optional().nullable(),
  eventTicket: z
    .array(eventTicketSchema)
    .min(1, 'At least one ticket is required for paid events')
    .refine(
      tickets => {
        const validTickets = tickets.filter(
          ticket =>
            ticket.name?.trim() &&
            ticket.description?.trim() &&
            ticket.perks?.trim() &&
            typeof ticket.price === 'string' &&
            Number(ticket.price) >= 0 &&
            Number(ticket.maxAttendeesLimit) > 0
        )
        return validTickets.length > 0
      },
      {
        message: 'At least one complete ticket is required for paid events',
      }
    ),
})

// Final discriminated union for registration type
export const registrationBasedEventSchema = z.discriminatedUnion(
  'registrationType',
  [freeRegistrationSchema, paidRegistrationSchema]
)

const eventDataSchema = locationBasedEventSchema.and(
  registrationBasedEventSchema
)

export const eventFormSchema = eventDataSchema

export const validateDates = (
  startDate: Date | null | undefined,
  startTime: Date | null | undefined,
  endDate: Date | null | undefined,
  endTime: Date | null | undefined,
  registrationDeadline: Date | null | undefined,
  mode: 'add' | 'edit' = 'add',
  touchedFields?: {
    startDate?: boolean
    startTime?: boolean
    endDate?: boolean
    endTime?: boolean
    registrationDeadline?: boolean
  },
  isSubmitting?: boolean
): { [key: string]: string } => {
  const errors: { [key: string]: string } = {}
  if (!startDate) {
    if (touchedFields?.startDate || isSubmitting) {
      errors.startDate = 'Start date is required'
    }
  } else if (Number.isNaN(startDate.getTime())) {
    errors.startDate = 'Please enter a valid start date'
  } else if (mode === 'add') {
    const today = normalizeDate(new Date())
    const checkDate = normalizeDate(startDate)
    if (checkDate < today) {
      errors.startDate = 'Event start date must be today or in the future'
    }
  }

  if (!startTime) {
    if (touchedFields?.startTime || isSubmitting) {
      errors.startTime = 'Start time is required'
    }
  } else if (Number.isNaN(startTime.getTime())) {
    errors.startTime = 'Please enter a valid start time'
  }

  if (!endDate) {
    if (touchedFields?.endDate || isSubmitting) {
      errors.endDate = 'End date is required'
    }
  } else if (Number.isNaN(endDate.getTime())) {
    errors.endDate = 'Please enter a valid end date'
  } else if (startDate) {
    const start = normalizeDate(startDate)
    const end = normalizeDate(endDate)
    if (end < start) {
      errors.endDate = 'End date must be on or after start date'
    }
  }

  if (!endTime) {
    if (touchedFields?.endTime || isSubmitting) {
      errors.endTime = 'End time is required'
    }
  } else if (Number.isNaN(endTime.getTime())) {
    errors.endTime = 'Please enter a valid end time'
  } else if (
    startTime &&
    endTime &&
    startDate &&
    endDate &&
    endTime.getTime() <= startTime.getTime()
  ) {
    const start = normalizeDate(startDate)
    const end = normalizeDate(endDate)
    const today = new Date()
    const currentDate = normalizeDate(today)
    const currentTime = today.getTime()
    if (
      start.getTime() == currentDate.getTime() &&
      currentTime >= startTime.getTime()
    ) {
      errors.startTime = 'Start time must be in future'
    }

    if (start.getTime() == end.getTime()) {
      errors.endTime = 'End time must be after start time'
    }
  }

  if (!registrationDeadline) {
    if (touchedFields?.registrationDeadline || isSubmitting) {
      errors.registrationDeadline = 'Registration deadline is required'
    }
  } else if (Number.isNaN(registrationDeadline.getTime())) {
    errors.registrationDeadline = 'Please enter a valid registration deadline'
  } else if (endDate) {
    const deadline = normalizeDate(registrationDeadline)
    const end = normalizeDate(endDate)
    if (deadline >= end) {
      errors.registrationDeadline =
        'Registration deadline must be before event end date'
    }
  }

  return errors
}

export const defaultEventTickets = [
  {
    id: '',
    description: '',
    name: '',
    perks: '',
    price: '',
    isNew: true,
    maxAttendeesLimit: '',
    isSubEventTicket: false,
    subEventId: null,
  },
]

export const defaultEventUsers = [
  {
    id: '',
    fullName: '',
    designation: '',
    isNew: true,
  },
]

export const defaultPresentingPlan = {
  name: SPONSORSHIP_PLANS.PRESENTING,
  price: '',
}
export const defaultProgramOwnerPlan = {
  name: SPONSORSHIP_PLANS.PROGRAM_OWNER,
  price: '',
}
export const defaultSupportingPlan = {
  name: SPONSORSHIP_PLANS.SUPPORTING,
  price: '',
}
export const defaultNamingSponsorPlan = {
  name: SPONSORSHIP_PLANS.NAMING_SPONSOR,
  price: '',
  heroDuration: '',
}

export const defaultSponsorshipPackages = [
  {
    id: '',
    name: '',
    description: '',
    price: '',
    availablePlacements: [],
    priority: '',
    heroDuration: '',
    maxSponsorshipsLimit: '',
    isNew: true,
  },
]

export const defaultActivityHighlights = [
  {
    id: '',
    title: '',
    shortDescription: '',
    eventImage: null,
    startDate: undefined,
    endDate: undefined,
    isNew: true,
  },
]

export type EventFormDataType = z.infer<typeof eventFormSchema>
export type EventTicketType = z.infer<typeof eventTicketSchema>
export interface EventSponsorShipPayloadType {
  id?: string
  name: string
  price?: number | string | null
  heroDuration?: number | string | null
  description: string
  priority?: number | string | null
  availablePlacements: Array<string>
  maxSponsorshipsLimit?: number | string | null
}
